import React from 'react';
import { Users, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function Pricing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-700">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-white" />
              <span className="ml-2 text-2xl font-bold text-white">EZ-CRM</span>
            </div>
            <button 
              onClick={() => navigate('/')}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-400 transition-colors"
            >
              ← Return to Home Page
            </button>
          </div>
        </nav>

        <div className="container mx-auto px-6 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              Choose Your Plan
            </h1>
            <p className="mt-6 text-xl text-blue-100 max-w-2xl mx-auto">
              Simple, transparent pricing that grows with your business. All plans include a 14-day free trial.
            </p>
          </div>
        </div>
      </header>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <PricingCard 
              title="Starter"
              price="5"
              features={[
                "Up to 1,000 contacts",
                "Basic reporting",
                "Email integration",
                "5 team members",
                "Standard support",
                "Mobile app access",
                "Basic API access",
                "Weekly backups"
              ]}
              onGetStarted={() => navigate('/auth')}
            />
            <PricingCard 
              title="Professional"
              price="10"
              featured={true}
              features={[
                "Up to 10,000 contacts",
                "Advanced analytics",
                "Full API access",
                "Unlimited team members",
                "Priority support",
                "Custom dashboards",
                "Integration with popular tools",
                "Daily backups"
              ]}
              onGetStarted={() => navigate('/auth')}
            />
            <PricingCard 
              title="Enterprise"
              price="20"
              features={[
                "Unlimited contacts",
                "Custom reporting",
                "24/7 dedicated support",
                "Custom integrations",
                "SLA guarantee",
                "Advanced security features",
                "Custom API development",
                "Real-time backups"
              ]}
              onGetStarted={() => navigate('/auth')}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-16">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-8">
            <FAQItem 
              question="Can I switch plans later?"
              answer="Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle."
            />
            <FAQItem 
              question="What payment methods do you accept?"
              answer="We accept all major credit cards, PayPal, and bank transfers for annual plans."
            />
            <FAQItem 
              question="Is there a long-term contract?"
              answer="No, all plans are month-to-month with no long-term commitment required. Annual plans are available at a discount."
            />
            <FAQItem 
              question="What's included in the free trial?"
              answer="The 14-day free trial includes all features of the Professional plan, allowing you to fully test our platform."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400">&copy; 2025 EZ-CRM. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function PricingCard({ title, price, features, featured = false, onGetStarted }) {
  return (
    <div className={`p-8 rounded-lg ${featured ? 'bg-blue-600 text-white ring-4 ring-blue-200' : 'bg-white shadow-lg'}`}>
      <h3 className="text-2xl font-bold mb-4">{title}</h3>
      <div className="flex items-baseline mb-8">
        <span className="text-4xl font-bold">${price}</span>
        <span className="ml-2 opacity-75">/user/month</span>
      </div>
      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center">
            <CheckCircle2 className={`h-5 w-5 ${featured ? 'text-white' : 'text-blue-600'} mr-2`} />
            {feature}
          </li>
        ))}
      </ul>
      <button 
        onClick={onGetStarted}
        className={`w-full py-3 rounded-lg font-semibold transition-colors ${
          featured 
            ? 'bg-white text-blue-600 hover:bg-blue-50' 
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        Get Started
      </button>
    </div>
  );
}

function FAQItem({ question, answer }) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{question}</h3>
      <p className="text-gray-600">{answer}</p>
    </div>
  );
}

export default Pricing;
